import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { ChevronDown, ChevronUp, Search, Package, Download, Clock, CheckCircle, Truck, XCircle, FileText, Plus, Edit2, X, ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WarehouseManagement = ({ token, clients, onNavigateToInvoice, onNavigate, navigationParams }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  
  // Create WR Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [trackings, setTrackings] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientTrackings, setClientTrackings] = useState([]);
  const [selectedTrackingIds, setSelectedTrackingIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [clientComboOpen, setClientComboOpen] = useState(false);
  const [editClientComboOpen, setEditClientComboOpen] = useState(false);
  const [filterClientComboOpen, setFilterClientComboOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  
  // Edit WR Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWR, setEditingWR] = useState(null);
  const [editWrForm, setEditWrForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // WR Form with dimensions separated for auto-calculation
  const [wrFormData, setWrFormData] = useState({
    wr_number: '',     // Optional: assign WR number
    shipping_method: 'Aereo',
    package_type: 'Caja',
    pieces: 1,
    // Dimensions in inches (separated for calculation)
    dim_largo: '',    // Length
    dim_ancho: '',    // Width  
    dim_alto: '',     // Height
    // Weights
    pp_weight_lb: '',  // Peso Pesado (real weight in lb)
    pvol: '',          // Peso Volumétrico (calculated for Aereo)
    pmax: '',          // PMax (the higher of pp or pvol)
    // Volume for Maritimo
    volume_ft3: '',    // Volume in ft³ (calculated for Maritimo)
    // Other
    notes: '',
    agent: 'Ninguno',
    description: ''
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    cancellationStatus: 'Activo',
    destinationCountry: 'todos',
    destinationState: 'todos',
    consolidated: 'todos',
    userId: 'todos',
    dateFrom: '',
    dateTo: '',
    shippingMethod: 'todos',
    finished: 'todos',
    agency: 'todas',
    agent: 'cualquiera',
    repackaged: 'todos'
  });
  
  const [showUnprocessed, setShowUnprocessed] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    if (navigationParams?.filterClientId) {
      setFilters(prev => ({ ...prev, userId: navigationParams.filterClientId }));
    }
    if (navigationParams?.searchWr) {
      setFilters(prev => ({ ...prev, search: navigationParams.searchWr }));
    }
  }, [navigationParams]);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  // Filter clients based on search for "Crear WR desde Trackings"
  useEffect(() => {
    if (clientSearch.length > 0) {
      const search = clientSearch.toLowerCase();
      const filtered = (clients || []).filter(c =>
        c.full_name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.locker_number?.toLowerCase().includes(search)
      );
      setFilteredClients(filtered.slice(0, 8));
      setShowClientDropdown(true);
    } else {
      setFilteredClients([]);
      setShowClientDropdown(false);
    }
  }, [clientSearch, clients]);

  const selectClientForWR = (client) => {
    setSelectedClientId(client.id);
    setClientSearch(client.full_name + ' - ' + (client.locker_number || client.email));
    setShowClientDropdown(false);
  };

  useEffect(() => {
    fetchWarehouses();
    fetchAllTrackings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [warehouses, filters, showUnprocessed]);

  // Fetch all trackings for the create WR modal
  const fetchAllTrackings = async () => {
    try {
      const response = await axios.get(`${API}/admin/trackings`, axiosConfig);
      setTrackings(response.data);
    } catch (error) {
    }
  };

  // Filter trackings by selected client
  useEffect(() => {
    if (selectedClientId) {
      const filtered = trackings.filter(t => 
        t.client_id === selectedClientId && 
        !t.consolidated && 
        t.status !== 'in_warehouse'
      );
      setClientTrackings(filtered);
      setSelectedTrackingIds([]);
    } else {
      setClientTrackings([]);
      setSelectedTrackingIds([]);
    }
  }, [selectedClientId, trackings]);

  // Calculate total weight when trackings are selected
  useEffect(() => {
    if (selectedTrackingIds.length > 0) {
      const selectedTracks = clientTrackings.filter(t => selectedTrackingIds.includes(t.id));
      const totalWeight = selectedTracks.reduce((sum, t) => sum + (parseFloat(t.weight) || 0), 0);
      setWrFormData(prev => ({
        ...prev,
        pp_weight_lb: totalWeight.toFixed(2)
      }));
    }
  }, [selectedTrackingIds, clientTrackings]);

  // Toggle tracking selection
  const toggleTrackingSelection = (trackingId) => {
    setSelectedTrackingIds(prev => {
      if (prev.includes(trackingId)) {
        return prev.filter(id => id !== trackingId);
      } else {
        return [...prev, trackingId];
      }
    });
  };

  // Select/Deselect all trackings
  const toggleAllClientTrackings = () => {
    if (selectedTrackingIds.length === clientTrackings.length) {
      setSelectedTrackingIds([]);
    } else {
      setSelectedTrackingIds(clientTrackings.map(t => t.id));
    }
  };

  // Calculate PVol (Peso Volumétrico) for AEREO = (L x W x H) / 166
  // Calculate Volume ft³ for MARITIMO = (L x W x H) / 1728
  const calculateVolumetrics = (largo, ancho, alto, shippingMethod) => {
    const l = parseFloat(largo) || 0;
    const w = parseFloat(ancho) || 0;
    const h = parseFloat(alto) || 0;
    
    if (l === 0 || w === 0 || h === 0) {
      return { pvol: 0, volume_ft3: 0 };
    }
    
    const cubicInches = l * w * h;
    
    // AEREO: PVol = cubic inches / 166
    const pvol = cubicInches / 166;
    
    // MARITIMO: Volume ft³ = cubic inches / 1728
    const volume_ft3 = cubicInches / 1728;
    
    return { pvol: pvol.toFixed(2), volume_ft3: volume_ft3.toFixed(2) };
  };

  // Handle dimension changes with auto-calculation
  const handleDimensionChange = (field, value) => {
    const newFormData = { ...wrFormData, [field]: value };
    
    // Recalculate volumetrics
    const { pvol, volume_ft3 } = calculateVolumetrics(
      field === 'dim_largo' ? value : newFormData.dim_largo,
      field === 'dim_ancho' ? value : newFormData.dim_ancho,
      field === 'dim_alto' ? value : newFormData.dim_alto,
      newFormData.shipping_method
    );
    
    newFormData.pvol = pvol;
    newFormData.volume_ft3 = volume_ft3;
    
    // Calculate PMax (the higher of PP weight or PVol for Aereo)
    const pp = parseFloat(newFormData.pp_weight_lb) || 0;
    const pvolNum = parseFloat(pvol) || 0;
    newFormData.pmax = Math.max(pp, pvolNum).toFixed(2);
    
    setWrFormData(newFormData);
  };

  // Handle PP (Peso Pesado) weight change
  const handlePPWeightChange = (value) => {
    const pp = parseFloat(value) || 0;
    const pvolNum = parseFloat(wrFormData.pvol) || 0;
    
    setWrFormData(prev => ({
      ...prev,
      pp_weight_lb: value,
      pmax: Math.max(pp, pvolNum).toFixed(2)
    }));
  };

  // Handle shipping method change
  const handleShippingMethodChange = (method) => {
    const { pvol, volume_ft3 } = calculateVolumetrics(
      wrFormData.dim_largo,
      wrFormData.dim_ancho,
      wrFormData.dim_alto,
      method
    );
    
    const pp = parseFloat(wrFormData.pp_weight_lb) || 0;
    const pvolNum = parseFloat(pvol) || 0;
    
    setWrFormData(prev => ({
      ...prev,
      shipping_method: method,
      pvol,
      volume_ft3,
      pmax: Math.max(pp, pvolNum).toFixed(2)
    }));
  };

  // Open create modal
  const openCreateModal = () => {
    setShowCreateModal(true);
    setSelectedClientId('');
    setClientSearch('');
    setShowClientDropdown(false);
    setSelectedTrackingIds([]);
    setWrFormData({
      wr_number: '',
      shipping_method: 'Aereo',
      package_type: 'Caja',
      pieces: 1,
      dim_largo: '',
      dim_ancho: '',
      dim_alto: '',
      pp_weight_lb: '',
      pvol: '',
      pmax: '',
      volume_ft3: '',
      notes: '',
      agent: 'Ninguno',
      description: ''
    });
  };

  // Create WR from selected trackings
  const handleCreateWR = async () => {
    if (!selectedClientId) {
      alert('Debe seleccionar un cliente');
      return;
    }
    if (selectedTrackingIds.length === 0) {
      alert('Debe seleccionar al menos un tracking');
      return;
    }
    if (!wrFormData.dim_largo || !wrFormData.dim_ancho || !wrFormData.dim_alto) {
      alert('Debe ingresar las dimensiones del paquete (Largo x Ancho x Alto)');
      return;
    }
    if (!wrFormData.pp_weight_lb) {
      alert('Debe ingresar el peso (PP) del paquete');
      return;
    }

    setCreating(true);
    try {
      // Build dimensions string
      const dimensionsIn = `${wrFormData.dim_largo}x${wrFormData.dim_ancho}x${wrFormData.dim_alto}`;
      
      // For AEREO use pvol, for MARITIMO use volume_ft3
      const isAereo = wrFormData.shipping_method === 'Aereo';
      
      const response = await axios.post(
        `${API}/admin/warehouse-receipts/from-trackings`,
        {
          client_id: selectedClientId,
          tracking_ids: selectedTrackingIds,
          wr_number: wrFormData.wr_number || undefined,
          shipping_method: wrFormData.shipping_method,
          package_type: wrFormData.package_type,
          pieces: parseInt(wrFormData.pieces) || 1,
          final_weight_lb: parseFloat(wrFormData.pp_weight_lb) || 0,
          final_dimensions_in: dimensionsIn,
          volume: parseFloat(wrFormData.volume_ft3) || 0,
          volumetric_weight: parseFloat(wrFormData.pvol) || 0,
          pmax: parseFloat(wrFormData.pmax) || 0,
          notes: wrFormData.notes,
          agent: wrFormData.agent,
          description: wrFormData.description
        },
        axiosConfig
      );
      
      alert(`✅ ${response.data.message}\nWR: ${response.data.wr_number}`);
      setShowCreateModal(false);
      fetchWarehouses();
      fetchAllTrackings();
    } catch (error) {
      alert('Error al crear WR: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreating(false);
    }
  };

  // Get client name helper
  const getClientName = (clientId) => {
    const client = clients?.find(c => c.id === clientId);
    return client ? client.full_name : clientId;
  };

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/warehouse-receipts`, axiosConfig);
      setWarehouses(response.data);
      setSelectedWarehouses([]); // Clear selection after refresh
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Toggle selection of a single WR
  const toggleSelectWarehouse = (wrId) => {
    setSelectedWarehouses(prev => {
      if (prev.includes(wrId)) {
        return prev.filter(id => id !== wrId);
      } else {
        return [...prev, wrId];
      }
    });
  };

  // Select/Deselect all WRs
  const toggleSelectAll = () => {
    if (selectedWarehouses.length === filteredWarehouses.length) {
      setSelectedWarehouses([]);
    } else {
      setSelectedWarehouses(filteredWarehouses.map(w => w.id));
    }
  };

  // Delete multiple selected WRs
  const handleDeleteSelected = async () => {
    if (selectedWarehouses.length === 0) {
      alert('No hay warehouse receipts seleccionados');
      return;
    }
    
    const confirmMsg = `¿Estás seguro de eliminar ${selectedWarehouses.length} warehouse receipt(s)?\nEsta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    
    setLoading(true);
    let deleted = 0;
    let errors = 0;
    
    for (const wrId of selectedWarehouses) {
      try {
        await axios.delete(`${API}/admin/warehouse-receipts/${wrId}`, axiosConfig);
        deleted++;
      } catch (error) {
        errors++;
      }
    }
    
    alert(`✅ Eliminados: ${deleted}\n${errors > 0 ? `❌ Errores: ${errors}` : ''}`);
    setSelectedWarehouses([]);
    fetchWarehouses();
  };

  // Open Edit WR Modal
  const openEditWrModal = (wr) => {
    setEditingWR(wr);
    setEditWrForm({
      wr_number: wr.wr_number || '',
      client_id: wr.client_id || '',
      shipping_method: wr.shipping_method || 'Aereo',
      package_type: wr.package_type || 'Caja',
      pieces: wr.pieces || 1,
      final_weight_lb: wr.final_weight_lb || '',
      volumetric_weight_lb: wr.volumetric_weight_lb || wr.volumetric_weight || '',
      volume: wr.volume || '',
      final_dimensions_in: wr.final_dimensions_in || '',
      notes: wr.notes || '',
      instructions: wr.instructions || '',
      agency: wr.agency || '',
      agent: wr.agent || '',
      // Client data for manual WRs
      client_name: wr.client_name || '',
      client_address: wr.destination_address || '',
      destination_city: wr.destination_city || '',
      destination_state: wr.destination_state || ''
    });
    setShowEditModal(true);
  };

  // Save WR Edit
  const handleSaveWrEdit = async () => {
    if (!editingWR) return;
    
    setSaving(true);
    try {
      const updateData = {};
      
      // Only include changed fields
      if (editWrForm.wr_number && editWrForm.wr_number !== editingWR.wr_number) {
        updateData.wr_number = editWrForm.wr_number;
      }
      if (editWrForm.shipping_method !== editingWR.shipping_method) {
        updateData.shipping_method = editWrForm.shipping_method;
      }
      if (editWrForm.package_type !== editingWR.package_type) {
        updateData.package_type = editWrForm.package_type;
      }
      if (editWrForm.pieces) {
        updateData.pieces = parseInt(editWrForm.pieces);
      }
      if (editWrForm.final_weight_lb) {
        updateData.final_weight_lb = parseFloat(editWrForm.final_weight_lb);
      }
      if (editWrForm.volumetric_weight_lb) {
        updateData.volumetric_weight_lb = parseFloat(editWrForm.volumetric_weight_lb);
        updateData.volumetric_weight = parseFloat(editWrForm.volumetric_weight_lb);
      }
      if (editWrForm.volume) {
        updateData.volume = parseFloat(editWrForm.volume);
      }
      if (editWrForm.final_dimensions_in) {
        updateData.final_dimensions_in = editWrForm.final_dimensions_in;
      }
      if (editWrForm.notes !== editingWR.notes) {
        updateData.notes = editWrForm.notes;
      }
      if (editWrForm.instructions !== editingWR.instructions) {
        updateData.instructions = editWrForm.instructions;
      }
      if (editWrForm.agency) {
        updateData.agency = editWrForm.agency;
      }
      if (editWrForm.agent) {
        updateData.agent = editWrForm.agent;
      }
      // Check if client was changed
      if (editWrForm.client_id && editWrForm.client_id !== editingWR.client_id) {
        updateData.client_id = editWrForm.client_id;
      }
      // Include client data for manual WRs
      if (editingWR.client_id === 'manual') {
        if (editWrForm.client_name) updateData.client_name = editWrForm.client_name;
        if (editWrForm.client_address) updateData.destination_address = editWrForm.client_address;
      }
      if (editWrForm.destination_city) updateData.destination_city = editWrForm.destination_city;
      if (editWrForm.destination_state) updateData.destination_state = editWrForm.destination_state;
      
      await axios.put(`${API}/admin/warehouse-receipts/${editingWR.id}`, updateData, axiosConfig);
      
      alert('✅ Warehouse Receipt actualizado exitosamente');
      setShowEditModal(false);
      setEditingWR(null);
      fetchWarehouses();
    } catch (error) {
      alert('Error al actualizar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Helper function para obtener el estilo del estado del WR (sin JSX para evitar loops)
  const getWRStatusStyle = (status) => {
    const styles = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        iconType: 'Clock',
        label: '⏳ En Proceso'
      },
      invoiced: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        iconType: 'FileText',
        label: '📄 Facturado'
      },
      paid: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        iconType: 'CheckCircle',
        label: '✅ Pagado'
      },
      shipped: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        iconType: 'Truck',
        label: '🚚 Enviado'
      },
      in_transit: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-800',
        iconType: 'Truck',
        label: '🛫 En Tránsito'
      },
      delivered: {
        bg: 'bg-green-200',
        text: 'text-green-900',
        iconType: 'CheckCircle',
        label: '📦 Entregado'
      },
      rejected: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        iconType: 'XCircle',
        label: '❌ Rechazado'
      }
    };
    
    return styles[status] || {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      iconType: null,
      label: status
    };
  };
  
  // Renderizar el icono basado en el tipo
  const renderWRIcon = (iconType) => {
    if (!iconType) return null;
    const iconProps = { className: "w-3 h-3 inline mr-1" };
    
    switch(iconType) {
      case 'Clock': return <Clock {...iconProps} />;
      case 'FileText': return <FileText {...iconProps} />;
      case 'CheckCircle': return <CheckCircle {...iconProps} />;
      case 'Truck': return <Truck {...iconProps} />;
      case 'XCircle': return <XCircle {...iconProps} />;
      default: return null;
    }
  };


  const applyFilters = () => {
    let filtered = [...warehouses];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(w =>
        w.wr_number?.toLowerCase().includes(searchLower) ||
        w.sender?.toLowerCase().includes(searchLower) ||
        w.recipient?.toLowerCase().includes(searchLower)
      );
    }

    // Cancellation status
    if (filters.cancellationStatus !== 'todos') {
      filtered = filtered.filter(w => w.cancellation_status === filters.cancellationStatus);
    }

    // Destination filters
    if (filters.destinationCountry !== 'todos') {
      filtered = filtered.filter(w => w.destination_country === filters.destinationCountry);
    }

    if (filters.destinationState !== 'todos') {
      filtered = filtered.filter(w => w.destination_state === filters.destinationState);
    }

    // Consolidated
    if (filters.consolidated !== 'todos') {
      const isConsolidated = filters.consolidated === 'si';
      filtered = filtered.filter(w => w.consolidated === isConsolidated);
    }

    // User filter
    if (filters.userId !== 'todos') {
      filtered = filtered.filter(w => w.client_id === filters.userId);
    }

    // Shipping method
    if (filters.shippingMethod !== 'todos') {
      filtered = filtered.filter(w => w.shipping_method === filters.shippingMethod);
    }

    // Finished
    if (filters.finished !== 'todos') {
      const isFinished = filters.finished === 'si';
      filtered = filtered.filter(w => w.finished === isFinished);
    }

    // Agency
    if (filters.agency !== 'todas') {
      filtered = filtered.filter(w => w.agency === filters.agency);
    }

    // Show only unprocessed
    if (showUnprocessed) {
      filtered = filtered.filter(w => w.processing_status === 'No Procesado');
    }

    setFilteredWarehouses(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredWarehouses].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setFilteredWarehouses(sorted);
  };

  const toggleRow = (wrId) => {
    setExpandedRows(prev => ({
      ...prev,
      [wrId]: !prev[wrId]
    }));
  };

  const handleBatchAction = async (action) => {
    if (selectedWarehouses.length === 0) {
      alert('Selecciona al menos un warehouse');
      return;
    }

    let updateData = {};
    
    switch(action) {
      case 'procesar':
        updateData = { processing_status: 'Procesado' };
        break;
      case 'finalizar':
        updateData = { finished: true };
        break;
      case 'anular':
        updateData = { cancellation_status: 'Anulado' };
        break;
      default:
        return;
    }

    try {
      await axios.post(
        `${API}/admin/warehouse-receipts/batch-update`,
        {
          wr_ids: selectedWarehouses,
          update_data: updateData
        },
        axiosConfig
      );
      alert(`${selectedWarehouses.length} warehouses actualizados`);
      setSelectedWarehouses([]);
      fetchWarehouses();
    } catch (error) {
      alert('Error al actualizar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleConsolidate = async () => {
    if (selectedWarehouses.length < 2) {
      alert('Selecciona al menos 2 WRs para consolidar');
      return;
    }

    if (!window.confirm(`¿Consolidar ${selectedWarehouses.length} Warehouse Receipts en uno solo?`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/warehouse-receipts/consolidate`,
        selectedWarehouses,  // Send array of IDs directly
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'application/json'
          }
        }
      );
      alert(`✅ Consolidación exitosa!\n\nWR Consolidado: ${response.data.consolidated_wr_number}\nPeso total: ${response.data.total_weight_lb} lb\nVolumen total: ${response.data.total_volume} ft³`);
      setSelectedWarehouses([]);
      fetchWarehouses();
    } catch (error) {
      alert('Error al consolidar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateInvoice = (warehouse) => {
    // En lugar de crear la factura automáticamente, navegar al formulario de factura manual
    // con los datos del WR prellenados para que el usuario pueda agregar servicios extras
    if (onNavigateToInvoice) {
      onNavigateToInvoice(warehouse);
    } else {
      alert('Error: Navegación no disponible. Por favor recarga la página.');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <span className="text-gray-400">↕</span>;
    return sortConfig.direction === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package size={24} />
            Gestión de Warehouse
          </CardTitle>
          <Button 
            onClick={openCreateModal}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus size={16} className="mr-2" />
            Crear WR desde Trackings
          </Button>
        </CardHeader>
        <CardContent>
          {/* Filters Section */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="col-span-2">
              <Label>Búsqueda</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Número / Remitente / Destinatario"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Estado de Anulación</Label>
              <Select value={filters.cancellationStatus} onValueChange={(v) => setFilters({ ...filters, cancellationStatus: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Anulado">Anulado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>País Destino</Label>
              <Select value={filters.destinationCountry} onValueChange={(v) => setFilters({ ...filters, destinationCountry: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Venezuela">Venezuela</SelectItem>
                  <SelectItem value="Colombia">Colombia</SelectItem>
                  <SelectItem value="Panama">Panamá</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Estado Destino</Label>
              <Select value={filters.destinationState} onValueChange={(v) => setFilters({ ...filters, destinationState: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Zulia">Zulia</SelectItem>
                  <SelectItem value="Carabobo">Carabobo</SelectItem>
                  <SelectItem value="Miranda">Miranda</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Consolidado</Label>
              <Select value={filters.consolidated} onValueChange={(v) => setFilters({ ...filters, consolidated: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="si">Sí</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Cliente</Label>
              <Popover open={filterClientComboOpen} onOpenChange={setFilterClientComboOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="filter-wr-client-selector">
                    {filters.userId !== 'todos'
                      ? (() => { const c = clients?.find(cl => cl.id === filters.userId); return c ? c.full_name : 'Todos'; })()
                      : 'Todos'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por nombre, email o locker..." data-testid="filter-wr-client-search" />
                    <CommandList>
                      <CommandEmpty>No se encontró cliente.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="todos-mostrar-todos" onSelect={() => { setFilters({ ...filters, userId: 'todos' }); setFilterClientComboOpen(false); }}>
                          <Check className={cn("mr-2 h-4 w-4", filters.userId === 'todos' ? "opacity-100" : "opacity-0")} />
                          Todos
                        </CommandItem>
                        {clients.map(client => (
                          <CommandItem
                            key={client.id}
                            value={`${client.full_name} ${client.email} ${client.locker_number || ''}`}
                            onSelect={() => { setFilters({ ...filters, userId: client.id }); setFilterClientComboOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", filters.userId === client.id ? "opacity-100" : "opacity-0")} />
                            {client.full_name} <span className="text-xs text-muted-foreground ml-1">{client.locker_number || client.email}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Método de Envío</Label>
              <Select value={filters.shippingMethod} onValueChange={(v) => setFilters({ ...filters, shippingMethod: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Aereo">Aéreo</SelectItem>
                  <SelectItem value="Maritimo">Marítimo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Finalizado</Label>
              <Select value={filters.finished} onValueChange={(v) => setFilters({ ...filters, finished: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="si">Sí</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleBatchAction('procesar')}
                disabled={selectedWarehouses.length === 0}
              >
                Reempacar
              </Button>

              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleBatchAction('procesar')}
                disabled={selectedWarehouses.length === 0}
              >
                Reempacar Avanzado
              </Button>

              <Button 
                variant="outline" 
                size="sm"
                onClick={handleConsolidate}
                disabled={selectedWarehouses.length < 2}
              >
                Consolidar
              </Button>

              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDeleteSelected}
                disabled={selectedWarehouses.length === 0}
                className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
              >
                🗑️ Eliminar ({selectedWarehouses.length})
              </Button>

              <select
                className="px-3 py-2 border rounded-md text-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    handleBatchAction(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Acciones en Lote</option>
                <option value="procesar">Marcar Procesado</option>
                <option value="finalizar">Marcar Finalizado</option>
                <option value="anular">Anular</option>
              </select>

              <Button variant="outline" size="sm" disabled={selectedWarehouses.length === 0}>
                <Download className="mr-2" size={16} />
                Descargar Etiquetas
              </Button>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Label>Solo no procesados</Label>
              <button
                onClick={() => setShowUnprocessed(!showUnprocessed)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showUnprocessed ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showUnprocessed ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-2">
                    <input
                      type="checkbox"
                      checked={selectedWarehouses.length === filteredWarehouses.length && filteredWarehouses.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-2 text-left">Número</th>
                  <th className="p-2 text-left">Ubicación</th>
                  <th className="p-2 text-left">Remitente</th>
                  <th className="p-2 text-left">Destinatario</th>
                  <th className="p-2 text-left">Destino</th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('created_at')}>
                    Fecha <SortIcon column="created_at" />
                  </th>
                  <th className="p-2 text-left">Piezas</th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('final_weight_lb')}>
                    Peso (lb) <SortIcon column="final_weight_lb" />
                  </th>
                  <th className="p-2 text-left">PVol</th>
                  <th className="p-2 text-left">Volumen</th>
                  <th className="p-2 text-left">Estado</th>
                  <th className="p-2 text-left">Procesado</th>
                  <th className="p-2 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredWarehouses.map((warehouse) => (
                  <React.Fragment key={warehouse.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedWarehouses.includes(warehouse.id)}
                          onChange={() => toggleSelectWarehouse(warehouse.id)}
                        />
                      </td>
                      <td className="p-2">
                        <button onClick={() => toggleRow(warehouse.id)} className="text-blue-600">
                          {expandedRows[warehouse.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <span 
                          className="ml-2 font-mono text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                          onClick={() => onNavigate && onNavigate('wr-detail', { wrData: warehouse })}
                        >{warehouse.wr_number}</span>
                      </td>
                      <td className="p-2">{warehouse.location || '-'}</td>
                      <td className="p-2">{warehouse.sender || '-'}</td>
                      <td className="p-2">
                        <button onClick={() => onNavigate && onNavigate('clients', { filterClientId: warehouse.client_id })} className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer">
                          {getClientName(warehouse.client_id)}
                        </button>
                      </td>
                      <td className="p-2">
                        {warehouse.destination_city}, {warehouse.destination_state}
                      </td>
                      <td className="p-2 text-xs">{new Date(warehouse.created_at).toLocaleDateString()}</td>
                      <td className="p-2">{warehouse.pieces}</td>
                      <td className="p-2">{warehouse.final_weight_lb} lb</td>
                      <td className="p-2">{warehouse.volumetric_weight || '-'}</td>
                      <td className="p-2">{warehouse.volume ? `${warehouse.volume} ft³` : '-'}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                          warehouse.cancellation_status === 'Anulado' 
                            ? 'bg-red-100 text-red-800' 
                            : `${getWRStatusStyle(warehouse.status).bg} ${getWRStatusStyle(warehouse.status).text}`
                        }`}>
                          {warehouse.cancellation_status === 'Anulado' ? (
                            <>
                              <XCircle className="w-3 h-3 inline mr-1" />
                              Anulado
                            </>
                          ) : (
                            <>
                              {renderWRIcon(getWRStatusStyle(warehouse.status).iconType)}
                              {getWRStatusStyle(warehouse.status).label}
                            </>
                          )}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          warehouse.processing_status === 'Procesado' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {warehouse.processing_status}
                        </span>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const response = await axios.get(
                                  `${API}/admin/warehouse-receipts/${warehouse.id}/pdf`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  }
                                );
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `WR_${warehouse.wr_number}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (error) {
                                alert('Error al descargar PDF');
                              }
                            }}
                            className="text-xs w-full"
                            title="Descargar PDF"
                          >
                            📄 PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const response = await axios.get(
                                  `${API}/admin/warehouse-receipts/${warehouse.id}/label`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  }
                                );
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                const labelType = warehouse.shipping_method === 'Aereo' ? 'AEREO' : 'MARITIMO';
                                link.setAttribute('download', `LABEL_${warehouse.wr_number}_${labelType}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (error) {
                                alert('Error al descargar etiqueta');
                              }
                            }}
                            className={`text-xs w-full ${warehouse.shipping_method === 'Aereo' ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                            title={`Etiqueta ${warehouse.shipping_method}`}
                          >
                            🏷️ Etiqueta {warehouse.shipping_method === 'Aereo' ? '✈️' : '🚢'}
                          </Button>
                          {warehouse.status !== 'invoiced' && !warehouse.consolidated && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGenerateInvoice(warehouse)}
                              className="text-xs w-full"
                            >
                              Generar Factura
                            </Button>
                          )}
                          {warehouse.status === 'invoiced' && (
                            <button onClick={() => onNavigate && onNavigate('invoices', { searchWr: warehouse.wr_number })} className="text-xs text-green-600 hover:text-green-800 hover:underline cursor-pointer">✓ Facturado</button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (window.confirm(`¿Estás seguro de eliminar el WR ${warehouse.wr_number}? Esta acción no se puede deshacer.`)) {
                                try {
                                  await axios.delete(
                                    `${API}/admin/warehouse-receipts/${warehouse.id}`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  alert('Warehouse Receipt eliminado correctamente');
                                  fetchWarehouses();
                                } catch (error) {
                                  alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
                                }
                              }
                            }}
                            className="text-xs w-full bg-red-50 text-red-700 hover:bg-red-100 border-red-200"
                            title="Eliminar WR"
                          >
                            🗑️ Eliminar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditWrModal(warehouse)}
                            className="text-xs w-full bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200 mt-1"
                            title="Editar WR"
                          >
                            ✏️ Editar
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[warehouse.id] && (
                      <tr className="bg-gray-50">
                        <td colSpan="14" className="p-4">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p><strong>Dimensiones:</strong> {warehouse.final_dimensions_in}</p>
                              <p><strong>Tipo:</strong> {warehouse.package_type}</p>
                              <p><strong>Método:</strong> {warehouse.shipping_method}</p>
                            </div>
                            <div>
                              <p><strong>Agencia:</strong> {warehouse.agency || 'N/A'}</p>
                              <p><strong>Agente:</strong> {warehouse.agent || 'N/A'}</p>
                              <p><strong>Consolidado:</strong> {warehouse.consolidated ? 'Sí' : 'No'}</p>
                            </div>
                            <div>
                              <p><strong>Trackings:</strong> {warehouse.tracking_numbers.length}</p>
                              <p><strong>Finalizado:</strong> {warehouse.finished ? 'Sí' : 'No'}</p>
                              <p><strong>Instrucciones:</strong> {warehouse.instructions || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {filteredWarehouses.length === 0 && (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <p className="text-gray-500 mt-2">No se encontraron warehouse receipts</p>
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Mostrando {filteredWarehouses.length} de {warehouses.length} warehouses
            {selectedWarehouses.length > 0 && ` | ${selectedWarehouses.length} seleccionados`}
          </div>
        </CardContent>
      </Card>

      {/* Modal Crear WR desde Trackings */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package size={24} className="text-green-600" />
                Crear Warehouse Receipt desde Trackings
              </h2>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Step 1: Select Client */}
            <div className="mb-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Search size={18} />
                  1. Seleccionar Cliente
                </h3>
                <div className="relative">
                  <Label className="text-blue-700">Buscar por nombre, email o código de casillero</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          if (selectedClientId && e.target.value === '') {
                            setSelectedClientId('');
                          }
                        }}
                        placeholder="Ej: TDA000100, juan@email.com, Juan Pérez..."
                        className="pl-10"
                        data-testid="create-wr-client-search"
                      />
                      <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    </div>
                  </div>
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map((client) => (
                        <div
                          key={client.id}
                          onClick={() => selectClientForWR(client)}
                          className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          data-testid={`create-wr-client-option-${client.id}`}
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            {client.full_name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{client.full_name}</p>
                            <p className="text-sm text-gray-500">{client.email}</p>
                          </div>
                          <div className="text-right">
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-mono">
                              {client.locker_number || 'Sin código'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedClientId && (
                  <div className="mt-3 bg-white border border-blue-300 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-600" size={20} />
                      <div>
                        <p className="font-semibold">{clients?.find(c => c.id === selectedClientId)?.full_name}</p>
                        <p className="text-sm text-gray-500">{clients?.find(c => c.id === selectedClientId)?.email}</p>
                      </div>
                    </div>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded font-mono font-bold">
                      {clients?.find(c => c.id === selectedClientId)?.locker_number || ''}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Select Trackings */}
            {selectedClientId && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-lg text-blue-700">2. Seleccionar Paquetes (Trackings)</h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={toggleAllClientTrackings}
                    disabled={clientTrackings.length === 0}
                  >
                    {selectedTrackingIds.length === clientTrackings.length 
                      ? 'Deseleccionar Todos' 
                      : 'Seleccionar Todos'}
                  </Button>
                </div>
                
                {clientTrackings.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left w-10">✓</th>
                          <th className="p-2 text-left">Tracking</th>
                          <th className="p-2 text-left">Descripción</th>
                          <th className="p-2 text-left">Peso (lb)</th>
                          <th className="p-2 text-left">Valor</th>
                          <th className="p-2 text-left">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientTrackings.map(tracking => (
                          <tr 
                            key={tracking.id} 
                            className={`border-t cursor-pointer hover:bg-gray-50 ${
                              selectedTrackingIds.includes(tracking.id) ? 'bg-green-50' : ''
                            }`}
                            onClick={() => toggleTrackingSelection(tracking.id)}
                          >
                            <td className="p-2">
                              <input 
                                type="checkbox" 
                                checked={selectedTrackingIds.includes(tracking.id)}
                                onChange={() => toggleTrackingSelection(tracking.id)}
                                className="w-4 h-4"
                              />
                            </td>
                            <td className="p-2 font-mono text-xs">{tracking.tracking_number}</td>
                            <td className="p-2">{tracking.description || 'Sin descripción'}</td>
                            <td className="p-2">{tracking.weight || '0'}</td>
                            <td className="p-2">${tracking.declared_value || '0'}</td>
                            <td className="p-2 text-xs">{new Date(tracking.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 text-center text-gray-500">
                    No hay trackings disponibles para este cliente
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  {selectedTrackingIds.length} de {clientTrackings.length} paquetes seleccionados
                </p>
              </div>
            )}

            {/* Step 3: Package Measurements */}
            {selectedTrackingIds.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-4 text-blue-700">3. Datos del Paquete Final (Consolidado)</h3>
                
                {/* WR Number (optional) */}
                <div className="bg-green-50 p-3 rounded border border-green-200 mb-4">
                  <Label className="text-sm font-semibold text-green-800">Numero de WR (Opcional)</Label>
                  <Input
                    data-testid="create-wr-number-input"
                    value={wrFormData.wr_number}
                    onChange={(e) => setWrFormData({...wrFormData, wr_number: e.target.value})}
                    placeholder="Dejar vacio para auto-generar (Ej: WR20XXX)"
                    className="mt-1"
                  />
                  <p className="text-xs text-green-600 mt-1">
                    Asigna el numero de WR de tu proveedor de cargas. Si lo dejas vacio se genera automaticamente.
                  </p>
                </div>

                {/* First Row: Method, Type, Pieces */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <Label>Método de Envío *</Label>
                    <Select 
                      value={wrFormData.shipping_method} 
                      onValueChange={handleShippingMethodChange}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aereo">✈️ Aéreo</SelectItem>
                        <SelectItem value="Maritimo">🚢 Marítimo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select 
                      value={wrFormData.package_type} 
                      onValueChange={(v) => setWrFormData({...wrFormData, package_type: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Caja">Caja</SelectItem>
                        <SelectItem value="Sobre">Sobre</SelectItem>
                        <SelectItem value="Paquete">Paquete</SelectItem>
                        <SelectItem value="Bulto">Bulto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cantidad</Label>
                    <Input 
                      type="number" 
                      min="1"
                      value={wrFormData.pieces}
                      onChange={(e) => setWrFormData({...wrFormData, pieces: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Agente</Label>
                    <Input 
                      value={wrFormData.agent}
                      onChange={(e) => setWrFormData({...wrFormData, agent: e.target.value})}
                      placeholder="Ninguno"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Dimensions Row - IN INCHES ONLY */}
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-3 text-blue-800">📐 Dimensiones (pulgadas / inches)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Largo (L) *</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        value={wrFormData.dim_largo}
                        onChange={(e) => handleDimensionChange('dim_largo', e.target.value)}
                        placeholder="Ej: 18"
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label>Ancho (W) *</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        value={wrFormData.dim_ancho}
                        onChange={(e) => handleDimensionChange('dim_ancho', e.target.value)}
                        placeholder="Ej: 14"
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label>Alto (H) *</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        value={wrFormData.dim_alto}
                        onChange={(e) => handleDimensionChange('dim_alto', e.target.value)}
                        placeholder="Ej: 12"
                        className="mt-1 bg-white"
                      />
                    </div>
                  </div>
                  {wrFormData.dim_largo && wrFormData.dim_ancho && wrFormData.dim_alto && (
                    <p className="text-sm text-blue-600 mt-2">
                      Dimensiones: {wrFormData.dim_largo}" x {wrFormData.dim_ancho}" x {wrFormData.dim_alto}"
                    </p>
                  )}
                </div>

                {/* Weights and Calculations */}
                <div className={`p-4 rounded-lg mb-4 ${wrFormData.shipping_method === 'Aereo' ? 'bg-sky-50' : 'bg-green-50'}`}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    {wrFormData.shipping_method === 'Aereo' ? '✈️' : '🚢'}
                    <span className={wrFormData.shipping_method === 'Aereo' ? 'text-sky-800' : 'text-green-800'}>
                      {wrFormData.shipping_method === 'Aereo' ? 'Cálculos Aéreo' : 'Cálculos Marítimo'}
                    </span>
                  </h4>
                  
                  {wrFormData.shipping_method === 'Aereo' ? (
                    // AEREO: PP, PVol, PMax
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>PP - Peso Pesado (lb) *</Label>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={wrFormData.pp_weight_lb}
                          onChange={(e) => handlePPWeightChange(e.target.value)}
                          placeholder="Peso real en libras"
                          className="mt-1 bg-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">Peso real de la caja</p>
                      </div>
                      <div>
                        <Label>PVol - Peso Volumétrico</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={wrFormData.pvol}
                          readOnly
                          className="mt-1 bg-gray-100 font-semibold"
                        />
                        <p className="text-xs text-gray-500 mt-1">(L×W×H) / 166</p>
                      </div>
                      <div>
                        <Label>PMax (Mayor)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={wrFormData.pmax}
                          readOnly
                          className="mt-1 bg-yellow-100 font-bold text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">El mayor entre PP y PVol</p>
                      </div>
                    </div>
                  ) : (
                    // MARITIMO: PP, Volume ft³
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>PP - Peso Pesado (lb) *</Label>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={wrFormData.pp_weight_lb}
                          onChange={(e) => handlePPWeightChange(e.target.value)}
                          placeholder="Peso real en libras"
                          className="mt-1 bg-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">Peso real de la caja</p>
                      </div>
                      <div>
                        <Label>Volumen (ft³)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={wrFormData.volume_ft3}
                          readOnly
                          className="mt-1 bg-green-100 font-bold text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">(L×W×H) / 1728</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description and Notes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Descripción</Label>
                    <Input 
                      value={wrFormData.description}
                      onChange={(e) => setWrFormData({...wrFormData, description: e.target.value})}
                      placeholder="Descripción del contenido"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Notas / Instrucciones</Label>
                    <Input 
                      value={wrFormData.notes}
                      onChange={(e) => setWrFormData({...wrFormData, notes: e.target.value})}
                      placeholder="Instrucciones especiales..."
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Summary Box */}
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <h4 className="font-semibold mb-2">📦 Resumen del Paquete</h4>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Tipo:</span>
                      <span className="ml-1 font-medium">{wrFormData.package_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Envío:</span>
                      <span className="ml-1 font-medium">{wrFormData.shipping_method}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Trackings:</span>
                      <span className="ml-1 font-medium">{selectedTrackingIds.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{wrFormData.shipping_method === 'Aereo' ? 'PMax:' : 'Vol ft³:'}</span>
                      <span className="ml-1 font-bold text-blue-600">
                        {wrFormData.shipping_method === 'Aereo' ? wrFormData.pmax || '0' : wrFormData.volume_ft3 || '0'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateWR}
                disabled={creating || selectedTrackingIds.length === 0 || !selectedClientId}
                className="bg-green-600 hover:bg-green-700"
              >
                {creating ? 'Creando WR...' : `✓ Crear WR (${selectedTrackingIds.length} trackings)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit WR Modal */}
      {showEditModal && editingWR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar WR: {editingWR.wr_number}</h2>
              <button onClick={() => { setShowEditModal(false); setEditingWR(null); }} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Editable WR Number */}
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <Label className="text-sm font-semibold text-blue-800">📋 Número de WR (Editable)</Label>
                <Input
                  value={editWrForm.wr_number}
                  onChange={(e) => setEditWrForm({...editWrForm, wr_number: e.target.value})}
                  className="mt-1"
                  placeholder="WR-2024-XXXX"
                />
                <p className="text-xs text-blue-600 mt-1">
                  Puedes cambiar el número para sincronizar con tu sistema externo
                </p>
              </div>

              {/* Client selector - allows changing the client for any WR */}
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <Label className="text-sm font-semibold text-green-800">👤 Cliente Asignado</Label>
                <Popover open={editClientComboOpen} onOpenChange={setEditClientComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={editClientComboOpen}
                      className="w-full justify-between font-normal mt-1"
                      data-testid="edit-wr-client-selector"
                    >
                      {editWrForm.client_id
                        ? (() => {
                            const c = clients?.find(cl => cl.id === editWrForm.client_id);
                            return c ? `${c.full_name} - ${c.locker_number || c.email}` : 'Seleccionar cliente';
                          })()
                        : 'Seleccionar cliente'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar por nombre, email o locker..." data-testid="edit-wr-client-search" />
                      <CommandList>
                        <CommandEmpty>No se encontró cliente.</CommandEmpty>
                        <CommandGroup>
                          {clients.map(client => (
                            <CommandItem
                              key={client.id}
                              value={`${client.full_name} ${client.email} ${client.locker_number || ''}`}
                              onSelect={() => {
                                const selectedClient = client;
                                setEditWrForm({
                                  ...editWrForm,
                                  client_id: selectedClient.id,
                                  client_name: selectedClient.full_name || ''
                                });
                                setEditClientComboOpen(false);
                              }}
                              data-testid={`edit-wr-client-option-${client.id}`}
                            >
                              <Check className={cn("mr-2 h-4 w-4", editWrForm.client_id === client.id ? "opacity-100" : "opacity-0")} />
                              <div className="flex flex-col">
                                <span className="font-medium">{client.full_name}</span>
                                <span className="text-xs text-muted-foreground">{client.email} {client.locker_number ? `| Locker: ${client.locker_number}` : ''}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-green-600 mt-1">
                  Puedes cambiar el cliente asociado a este WR
                </p>
              </div>

              {/* Client data for manual WRs */}
              {editingWR.client_id === 'manual' && (
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200 space-y-3">
                  <p className="text-sm font-semibold text-yellow-800">📝 Datos del Cliente (WR Manual)</p>
                  <div>
                    <Label className="text-xs">Nombre Cliente</Label>
                    <Input
                      value={editWrForm.client_name}
                      onChange={(e) => setEditWrForm({...editWrForm, client_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Dirección Entrega</Label>
                    <Input
                      value={editWrForm.client_address}
                      onChange={(e) => setEditWrForm({...editWrForm, client_address: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Método de Envío</Label>
                  <Select value={editWrForm.shipping_method} onValueChange={(v) => setEditWrForm({...editWrForm, shipping_method: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aereo">Aéreo</SelectItem>
                      <SelectItem value="Maritimo">Marítimo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tipo de Paquete</Label>
                  <Select value={editWrForm.package_type} onValueChange={(v) => setEditWrForm({...editWrForm, package_type: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Caja">Caja</SelectItem>
                      <SelectItem value="Sobre">Sobre</SelectItem>
                      <SelectItem value="Pallet">Pallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Piezas</Label>
                  <Input
                    type="number"
                    value={editWrForm.pieces}
                    onChange={(e) => setEditWrForm({...editWrForm, pieces: e.target.value})}
                    className="mt-1"
                    min="1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Peso (lb)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editWrForm.final_weight_lb}
                    onChange={(e) => setEditWrForm({...editWrForm, final_weight_lb: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">P.Vol (lb)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editWrForm.volumetric_weight_lb}
                    onChange={(e) => setEditWrForm({...editWrForm, volumetric_weight_lb: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Volumen (ft³)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editWrForm.volume}
                    onChange={(e) => setEditWrForm({...editWrForm, volume: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Dimensiones (LxAxA pulgadas)</Label>
                  <Input
                    value={editWrForm.final_dimensions_in}
                    onChange={(e) => setEditWrForm({...editWrForm, final_dimensions_in: e.target.value})}
                    className="mt-1"
                    placeholder="17x17x15"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ciudad Destino</Label>
                  <Input
                    value={editWrForm.destination_city}
                    onChange={(e) => setEditWrForm({...editWrForm, destination_city: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Estado Destino</Label>
                  <Input
                    value={editWrForm.destination_state}
                    onChange={(e) => setEditWrForm({...editWrForm, destination_state: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Notas</Label>
                <Textarea
                  value={editWrForm.notes}
                  onChange={(e) => setEditWrForm({...editWrForm, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-xs">Instrucciones</Label>
                <Textarea
                  value={editWrForm.instructions}
                  onChange={(e) => setEditWrForm({...editWrForm, instructions: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingWR(null); }}>
                Cancelar
              </Button>
              <Button onClick={handleSaveWrEdit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseManagement;
